import { BufferDocuments } from "@/components/fund/buffer-documents";
import { BufferHoldings } from "@/components/fund/buffer-holdings";
import { BufferOutcomeDetails } from "@/components/fund/buffer-outcome-details";
import { BufferOutcomePeriod } from "@/components/fund/buffer-outcome-period";
import {
  BufferPerformancePlaceholder,
  BufferPremiumDiscountPlaceholder,
} from "@/components/fund/buffer-perf-placeholder";
import { BufferRiskStrip } from "@/components/fund/buffer-risk-strip";
import { BufferScenarioTable } from "@/components/fund/buffer-scenario-table";
import { BufferSectionNav } from "@/components/fund/buffer-section-nav";
import { BufferStrategyStrip } from "@/components/fund/buffer-strategy-strip";
import { EtfNotFound } from "@/components/fund/etf-not-found";
import { FixedHoldingsTable } from "@/components/fund/fixed-holdings-table";
import { FixedSectionNav } from "@/components/fund/fixed-section-nav";
import { Fund2xRiskBanner } from "@/components/fund/fund-2x-risk-banner";
import { FundAllocationBreakdown } from "@/components/fund/fund-allocation-breakdown";
import { FundBreadcrumb } from "@/components/fund/fund-breadcrumb";
import { FundDetailsTable } from "@/components/fund/fund-details-table";
import { FundDistributionHistory } from "@/components/fund/fund-distribution-history";
import { FundHero } from "@/components/fund/fund-hero";
import { FundHoldingsTable } from "@/components/fund/fund-holdings-table";
import { FundIndexInfo } from "@/components/fund/fund-index-info";
import { FundStatTicker } from "@/components/fund/fund-research";
import { FundSectors } from "@/components/fund/fund-sectors";
import { LeveragedHoldingsTable } from "@/components/fund/leveraged-holdings-table";
import { LeveragedPerfChart } from "@/components/fund/leveraged-perf-chart";
import { LeveragedPremiumDiscount } from "@/components/fund/leveraged-premium-discount";
import { LeveragedSectionNav } from "@/components/fund/leveraged-section-nav";
import { LeveragedUnderlyingSecurity } from "@/components/fund/leveraged-underlying-security";
import { FundReasonsToConsider } from "@/components/fund/reasons-to-consider";
import { ThematicSectionNav } from "@/components/fund/thematic-section-nav";
import { EtfNewsletter } from "@/components/layout/EtfNewsletter";
import { ddbToFundDetail } from "@/data/fund-detail-mapper";
import type { FundDetail } from "@/data/fund-detail-types";
import {
  deriveLabelFromName,
  enrichBondHolding,
  parseBondNameFields,
} from "@/data/fund-details-bonds";
import { getBondFixedExtras } from "@/data/fund-details-fixed";
import { getLeveragedSectorBreakdown } from "@/data/lev2x-sector-breakdown";
import { useLiveHoldings } from "@/hooks/use-etf-live-holdings";
import { useLiveNav } from "@/hooks/use-etf-live-nav";
import { useFundBySlug } from "@/hooks/use-fund-list";
import { formatAsOfMdy, useFundLiveData } from "@/hooks/use-fund-live-data";
import { useLeveragedHoldings } from "@/hooks/use-leveraged-holdings";
import { fundImageUrl } from "@/lib/api-client";
import { isFundLiveHere } from "@/lib/env-visibility";
import { CATEGORY_DESCRIPTIONS } from "@/lib/fund-categories";
import { categoryListingPath } from "@/lib/fund-paths";
import { JsonLd, Seo, breadcrumbLd, financialProductLd } from "@/lib/seo";
/**
 * Generic, data-driven fund-detail page.
 *
 * Ported from the dane etf-website `fund/page.tsx` (`FundDetailInner`). The
 * page resolves a `FundDetail` by URL slug from the DDB fund list, then
 * branches on `fund.type` (thematic / leveraged / buffer / fixed).
 *
 * Data flow:
 *   1. `useFundBySlug(slug)` — looks up the raw DDB fund by `fund.id`.
 *   2. `ddbToFundDetail(ddbFund)` — maps DDB → FundDetail (all fields, no drops).
 *   3. `FundDetailInner` — renders using the full FundDetail shape.
 *
 * All feature flags (isLiveProduction, showPerformanceChart, showHoldingsTable,
 * etc.) live on the DDB fund record and are passed through via ddbToFundDetail.
 */
import { useParams } from "react-router-dom";

/**
 * Fixed-income extras the dane API layers on top of the static catalog.
 * Absent in the static replica (always null) — the FI sections render their
 * empty states. Kept as a local type so the FI branch reads identically to
 * the dane source.
 */
interface FundDetailFixedExtras {
  allocation_breakdown?: {
    credit_quality?: Array<{ label: string; pct: number; color: string }>;
    maturity?: Array<{ label: string; pct: number; color: string }>;
    industry?: Array<{ label: string; pct: number; color: string }>;
    geography?: Array<{ label: string; pct: number; color: string }>;
    as_of_date?: string | null;
    weighted_avg_maturity?: string | null;
  } | null;
  distribution_history?: Array<{
    ex_div_date: string;
    record_date: string;
    payable_date: string;
    nav: string;
    lt_cap_gain: string;
    st_cap_gain: string;
    dividend_income: string;
    return_of_capital: string;
    total_distribution: string;
  }> | null;
}

interface FundDetailRouteParams extends Record<string, string | undefined> {
  slug: string;
}

export function FundDetailPage() {
  const { slug: rawSlug } = useParams<FundDetailRouteParams>();
  const slug = rawSlug?.toLowerCase() ?? "";

  // Look up the fund by slug (= fund.id). `useFundBySlug` reuses the shared
  // ["funds"] cache when present, else fetches just this one fund.
  const { data: ddbFund, isPending, isError } = useFundBySlug(slug);

  // While loading, optimistically render nothing (avoids flash to 404).
  if (isPending) return null;

  // Two distinct failure modes, deliberately handled differently for SEO:
  //   • Definitively missing/not-live → emit `noindex` so Google drops the URL.
  //   • Indeterminate (network/5xx) → render the same card but DO NOT noindex,
  //     leaving the static `index,follow` default in place. A transient API blip
  //     must never deindex a real, live fund; Google re-renders it later.
  const definitivelyMissing = !isError && (!ddbFund || !isFundLiveHere(ddbFund));
  if (isError || definitivelyMissing) {
    return (
      <>
        {definitivelyMissing && (
          <Seo title="Fund Not Found" description="This fund could not be found." noindex />
        )}
        <EtfNotFound
          slug={slug}
          entityName="Fund"
          backLabel={<>Back to Products &rarr;</>}
          backHref="/products"
        />
      </>
    );
  }

  // Past the guards above `ddbFund` is always present; this narrows the type.
  if (!ddbFund) return null;

  // White-label redirect gate: if the fund has a safe http(s) redirect URL,
  // leave the site immediately — no detail page renders for white-label funds.
  // The window guard keeps this render-time side effect from crashing
  // renderToString; a static render of a white-label fund emits nothing.
  if (ddbFund.whiteLabelRedirectUrl && /^https?:\/\//i.test(ddbFund.whiteLabelRedirectUrl)) {
    if (typeof window !== "undefined") {
      window.location.replace(ddbFund.whiteLabelRedirectUrl);
    }
    return null;
  }

  // Map the DDB record to the full FundDetail display shape.
  const fund = ddbToFundDetail(ddbFund);

  // ── SEO ──────────────────────────────────────────────────────────────────
  // Per-fund title/description/share-image, FinancialProduct + breadcrumb
  // structured data. Prefers the editor-authored SEO fields on the DDB record,
  // then the short summary, then the long description.
  const fundName = ddbFund.fullName ?? ddbFund.name;
  const seoTitle = ddbFund.seoTitle ?? `${ddbFund.ticker} — ${fundName}`;
  const seoDescription =
    ddbFund.seoDescription ??
    ddbFund.summary ??
    ddbFund.description ??
    `${fundName} (${ddbFund.ticker}) — an ETF from Corgi Funds. View holdings, performance, expense ratio, and fund documents.`;
  const shareImage = ddbFund.ogImage ?? fundImageUrl(ddbFund.imageKeys?.[0]);
  const categoryLabel = CATEGORY_DESCRIPTIONS[ddbFund.fundType]?.title ?? "ETFs";

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonicalPath={`/${ddbFund.id}`}
        image={shareImage}
        type="product"
      />
      <JsonLd
        data={[
          financialProductLd({
            ticker: ddbFund.ticker,
            name: ddbFund.name,
            fullName: ddbFund.fullName,
            description: ddbFund.description,
            summary: ddbFund.summary,
            exchange: ddbFund.exchange,
            isin: ddbFund.isin,
            category: ddbFund.category,
            slug: ddbFund.id,
            image: shareImage,
          }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Products", path: "/products" },
            { name: categoryLabel, path: categoryListingPath(ddbFund.fundType) },
            { name: ddbFund.ticker },
          ]),
        ]}
      />
      <FundDetailInner fund={fund} />
    </>
  );
}

// Exported so the chrome-less admin preview route (PreviewPage) can render the
// exact same template from a draft DDB Fund without rebuilding any of it.
export function FundDetailInner({ fund }: { fund: FundDetail }) {
  // Pre-fetch the summary so React Query populates the cache before child
  // components (FundHero, FundDetailsTable) request it. When the backend has
  // no market data yet (ingestion not running), liveData is null — the page
  // still renders with full DDB metadata; NAV/AUM sections just show "—".
  // Passing `fund` merges the funds-list market overlay (price/premium/dates).
  const live = useFundLiveData(fund.ticker, fund);
  // Live holdings snapshot date (daily_nav/holdings position date) — used to
  // label the Holdings table with the real date instead of "Latest"/stale config.
  const liveHoldingsAsOf = formatAsOfMdy(live?.holdingsAsOfDate);

  const liveHoldings = useLiveHoldings(fund);
  const leveragedHoldings = useLeveragedHoldings(fund.type === "leveraged" ? fund.ticker : "");
  const { holdings, holdingsCount, dateLabel, isLive } = liveHoldings;

  // Underlying-fund live-holdings lookup for the lev-2x Sector Breakdown.
  // No live feed yet → empty; the template falls back to the fund's static sectors.
  const underlyingHoldings: typeof holdings = [];
  const underlyingHoldingsCount = 0;

  const { data: navData } = useLiveNav(fund);
  // ⚠️ HARDCODED bond allocation breakdown (ported from dane seed). De-hardcode
  // when the etfsite API serves allocation_breakdown per FI fund. Non-bond
  // tickers → null, so the empty-state path is preserved.
  const fixedExt: FundDetailFixedExtras | null = getBondFixedExtras(fund.ticker);

  // Feature flags — all live on the DDB fund record (no separate flags API).
  // ddbToFundDetail maps showPerformanceChart → canShowPerformance, etc.
  const showPerformanceChart = fund.canShowPerformance ?? false;
  const showHoldingsTable = fund.showHoldingsTable ?? false;

  const pdChartPoints = navData?.pdChartPoints;
  const pdDateLabels = navData?.pdDateLabels;
  const pdQuarterlyData = navData?.pdQuarterlyData;
  const pdDataPoints = navData?.pdDataPoints;

  const isLeveraged = fund.type === "leveraged";
  const isThematic = fund.type === "thematic";
  const isBuffer = fund.type === "buffer";
  const isFixed = fund.type === "fixed";

  if (isBuffer) {
    return (
      <>
        <BufferRiskStrip fund={fund} />
        <FundBreadcrumb fund={fund} />
        <FundHero fund={fund} />
        <BufferStrategyStrip fund={fund} />
        <BufferSectionNav />

        <div className="grid grid-cols-1 min-[901px]:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0 min-[901px]:border-r border-[var(--etf-border)]">
            <BufferOutcomePeriod fund={fund} />
            <BufferPerformancePlaceholder fund={fund} />
            <BufferPremiumDiscountPlaceholder
              fund={fund}
              chartPoints={pdChartPoints}
              dateLabels={pdDateLabels}
              quarterlyData={pdQuarterlyData}
              dataPoints={pdDataPoints}
            />
            <BufferScenarioTable fund={fund} />
            <BufferHoldings fund={fund} />
            <BufferDocuments fund={fund} />
          </div>

          <div className="sidebar bg-[var(--etf-black2)] flex flex-col">
            <FundDetailsTable
              fund={fund}
              factsOnly
              liveHoldings={liveHoldings}
              leveraged={leveragedHoldings}
            />
            <BufferOutcomeDetails fund={fund} />
          </div>
        </div>

        <EtfNewsletter />
      </>
    );
  }

  if (isFixed) {
    return (
      <>
        <FundBreadcrumb fund={fund} />
        <FundHero fund={fund} />
        <FundReasonsToConsider fund={fund} />
        <FundStatTicker fund={fund} />
        <FixedSectionNav />

        <div className="grid grid-cols-1 min-[901px]:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0 min-[901px]:border-r border-[var(--etf-border)]">
            <LeveragedPerfChart
              fund={fund}
              performanceData={showPerformanceChart ? navData?.performanceData : undefined}
              performanceHistory={showPerformanceChart ? navData?.performanceHistory : undefined}
            />

            <LeveragedPremiumDiscount
              fundType="fixed"
              hasPerformanceHistory={showPerformanceChart}
              chartPoints={pdChartPoints}
              dateLabels={pdDateLabels}
              quarterlyData={pdQuarterlyData}
              dataPoints={pdDataPoints}
            />

            <FundAllocationBreakdown
              breakdown={fixedExt?.allocation_breakdown}
              emptyTitle="Credit Quality"
              emptyContext="bond"
              asOfFallback={fund.dateLabel}
              maturityFallback={fund.maturityBandLabel}
            />

            <FundDistributionHistory
              rows={showPerformanceChart ? fixedExt?.distribution_history : undefined}
              fundName={fund.fullName}
              distributionFrequency={fund.distributionFrequency}
            />

            {showHoldingsTable && (
              <FixedHoldingsTable
                holdings={holdings.map((h) => {
                  const live = h as typeof h & {
                    coupon_rate?: string | null;
                    maturity_date?: string | null;
                    credit_rating?: string | null;
                  };
                  const enriched = enrichBondHolding({ cusip: h.cusip ?? "" });
                  const derived = deriveLabelFromName(h.name);
                  const parsed = parseBondNameFields(h.name);
                  return {
                    cusip: h.cusip ?? "",
                    issuerLabel: enriched.issuerLabel ?? derived.label,
                    fullName: h.name ?? enriched.fullName ?? derived.label,
                    pct: h.pct,
                    coupon: live.coupon_rate ?? enriched.coupon ?? parsed.coupon ?? null,
                    maturity: live.maturity_date ?? enriched.maturity ?? parsed.maturity ?? null,
                    rating:
                      (live.credit_rating || enriched.spRating || enriched.moodysRating) ?? null,
                  };
                })}
                holdingsCount={holdingsCount}
                dateLabel={
                  isLive
                    ? (liveHoldingsAsOf ?? dateLabel)
                    : (fund.holdingsAsOf ?? liveHoldingsAsOf ?? fund.dateLabel)
                }
                ticker={fund.ticker}
              />
            )}
          </div>

          <div className="bg-[var(--etf-black2)]">
            <FundDetailsTable
              fund={fund}
              liveHoldings={liveHoldings}
              leveraged={leveragedHoldings}
              betweenFactsAndDocuments={
                <FundIndexInfo
                  info={{
                    name: fund.indexName ?? null,
                    provider: fund.indexProvider ?? null,
                    ticker: null,
                    inception: fund.indexInceptionDate ?? null,
                    rebalance_frequency: fund.rebalanceFrequency ?? null,
                  }}
                />
              }
            />
          </div>
        </div>

        <EtfNewsletter />
      </>
    );
  }

  const topTenPct = holdings.slice(0, 10).reduce((s, h) => s + (h.pct ?? 0), 0);

  return (
    <>
      <FundBreadcrumb fund={fund} />

      {isLeveraged && <Fund2xRiskBanner fund={fund} />}

      <FundHero fund={fund} />

      <FundReasonsToConsider fund={fund} />

      <FundStatTicker fund={fund} />

      {isLeveraged && <LeveragedSectionNav />}
      {isThematic && <ThematicSectionNav />}

      <div className="grid grid-cols-1 min-[901px]:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 min-[901px]:border-r border-[var(--etf-border)]">
          {(isLeveraged || isThematic) && (
            <>
              <LeveragedPerfChart
                fund={fund}
                performanceData={showPerformanceChart ? navData?.performanceData : undefined}
                performanceHistory={showPerformanceChart ? navData?.performanceHistory : undefined}
              />
              <LeveragedPremiumDiscount
                fundType={isLeveraged ? "leveraged" : "thematic"}
                hasPerformanceHistory={showPerformanceChart}
                chartPoints={pdChartPoints}
                dateLabels={pdDateLabels}
                quarterlyData={pdQuarterlyData}
                dataPoints={pdDataPoints}
              />
            </>
          )}

          {isLeveraged ? (
            // #48: Render leveraged Holdings unconditionally — LeveragedHoldingsTable
            // self-manages the section anchor, swap box, and "Holdings unavailable" empty
            // state when hasLiveHoldings=false (swap-based single-stock funds). Without
            // this, the Holdings nav tab is orphaned on single-stock 2x funds.
            <LeveragedHoldingsTable
              fund={fund}
              fallbackDateLabel={liveHoldingsAsOf ?? fund.holdingsAsOf ?? fund.dateLabel}
              leveraged={leveragedHoldings}
              sectorBreakdownSlot={
                fund.leveragedSubtype === "single-stock" ? (
                  <LeveragedUnderlyingSecurity fund={fund} />
                ) : (
                  (() => {
                    // Per-underlying NAICS sector breakdown (ported from dane).
                    // Only third-party-ETF underlyings (VT/VOO/XLRE/SMH/…) are in
                    // the NAICS file; Corgi-internal underlyings → fund.sectors.
                    const naics = getLeveragedSectorBreakdown(fund.underlyingEtfTicker);
                    return (
                      <FundSectors
                        sectors={naics?.sectors ?? fund.sectors}
                        holdings={naics ? [] : underlyingHoldings}
                        holdingsCount={naics ? 0 : underlyingHoldingsCount}
                        topTenPct={naics ? undefined : topTenPct}
                        isUnderlying={isLeveraged}
                        underlyingSecurityType={fund.underlyingEtfName ? "ETF" : "common stock"}
                        sectorLabel={fund.sectorLabel}
                        tickLabel={fund.sectorTickLabel}
                        sectorCountLabel={fund.sectorCountLabel}
                        sectorFootnote={fund.sectorFootnote}
                      />
                    );
                  })()
                )
              }
            />
          ) : (
            showHoldingsTable && (
              <div id="sec-holdings">
                <FundHoldingsTable
                  holdings={holdings}
                  holdingsCount={holdingsCount}
                  dateLabel={
                    isLive
                      ? (liveHoldingsAsOf ?? dateLabel)
                      : (fund.holdingsAsOf ?? liveHoldingsAsOf ?? fund.dateLabel)
                  }
                  title="Holdings"
                  showFounder={Boolean(fund.founders)}
                  founderLookup={fund.founders}
                  ticker={fund.ticker}
                  sectorBreakdownSlot={
                    <FundSectors
                      sectors={fund.sectors}
                      holdings={holdings}
                      holdingsCount={holdingsCount}
                      topTenPct={topTenPct}
                      sectorLabel={fund.sectorLabel}
                      tickLabel={fund.sectorTickLabel}
                      sectorCountLabel={fund.sectorCountLabel}
                      sectorFootnote={fund.sectorFootnote}
                      sectorLayout={fund.sectorLayout ?? "grid"}
                    />
                  }
                />
              </div>
            )
          )}
        </div>

        <div className="bg-[var(--etf-black2)]">
          <FundDetailsTable fund={fund} liveHoldings={liveHoldings} leveraged={leveragedHoldings} />
        </div>
      </div>

      <EtfNewsletter />
    </>
  );
}
